import { registerMockAPI } from '~/mocks/mockApis';

import { useQuery } from '@tanstack/react-query';

import { axiosInstance } from '~/services';

import { API_ENDPOINT } from '~/constants';

import { receiveKeys } from './keys';

import type { TypeErrorResponseData } from '~/types/common.types';
import type {
  TypeGetReturnDisposalStatusHistoriesResponse,
  TypeReturnDisposalStatusHistoryApi,
} from '~/types/receive.types';

/**
 * [입고관리 > 회송/폐기 관리] 상태 변경 이력 조회 API
 * @note API에 페이지네이션 파라미터가 없어 전체를 반환하고 페이지네이션은 클라이언트에서 처리한다.
 */
const fetchReturnDisposalStatusHistories = async (
  inspectionId: number
): Promise<TypeReturnDisposalStatusHistoryApi[]> => {
  const { data } = await axiosInstance.get<TypeGetReturnDisposalStatusHistoriesResponse>(
    `${API_ENDPOINT.RECEIVE.GET_RETURN_DISPOSAL_ISSUES}/${inspectionId}/status-histories`
  );

  return data.data.histories;
};

type TypeUseGetReturnDisposalStatusHistoriesQueryParams = {
  inspectionId: number;
  enabled?: boolean;
};

/** [입고관리 > 회송/폐기 관리] 상태 변경 이력 조회 Query Hook */
export const useGetReturnDisposalStatusHistoriesQuery = ({
  inspectionId,
  enabled = true,
}: TypeUseGetReturnDisposalStatusHistoriesQueryParams) => {
  return useQuery({
    queryKey: receiveKeys.returnDisposalStatusHistories(inspectionId),
    queryFn: () => fetchReturnDisposalStatusHistories(inspectionId),
    enabled: enabled && !!inspectionId,
    staleTime: 0,
    gcTime: 1000 * 30,
  });
};

/** [입고관리 > 회송/폐기 관리] 상태 변경 이력 Mock (클레임 이력엔 증빙사진 URL 포함) */
registerMockAPI<TypeGetReturnDisposalStatusHistoriesResponse | TypeErrorResponseData>({
  method: 'get',
  endpoint: `${API_ENDPOINT.RECEIVE.GET_RETURN_DISPOSAL_ISSUES}/:inspectionId/status-histories`,
  label: '회송폐기관리/상태변경이력 조회',
  scenarios: [
    {
      name: '정상 응답 (13건)',
      response: {
        data: {
          histories: [
            { historyId: 13, inspectionLineStatus: '회송(진행중)', operatedBy: '와이즐리(woodong)', reason: '회수 신청', operatedAt: '2026-03-15T18:30:00', claimImageUrls: null },
            { historyId: 12, inspectionLineStatus: '회송(정보입력대기)', operatedBy: '컬리', reason: '보류해제-회송', operatedAt: '2026-03-15T16:10:00', claimImageUrls: null },
            { historyId: 11, inspectionLineStatus: '보류(보류)', operatedBy: '컬리', reason: '클레임 반려 (증빙 부족)', operatedAt: '2026-03-14T11:05:00', claimImageUrls: null },
            { historyId: 10, inspectionLineStatus: '클레임신청(클레임신청)', operatedBy: '와이즐리(woodong)', reason: '클레임 신청 (한글 표시 누락 확인 요청 - 재검토 필요)', operatedAt: '2026-03-13T10:30:00', claimImageUrls: ['https://placehold.co/600x400?text=%EA%B1%B0%EB%9E%98%EB%AA%85%EC%84%B8%EC%84%9C1', 'https://placehold.co/600x400?text=%EA%B1%B0%EB%9E%98%EB%AA%85%EC%84%B8%EC%84%9C2'] },
            { historyId: 9, inspectionLineStatus: '보류(보류)', operatedBy: '컬리', reason: '보류 지정 (검수 재확인 필요)', operatedAt: '2026-03-12T09:00:00', claimImageUrls: null },
            { historyId: 8, inspectionLineStatus: '폐기(진행중)', operatedBy: '시스템', reason: '자동 폐기 (미회수 자동폐기)', operatedAt: '2026-03-11T00:00:00', claimImageUrls: null },
            { historyId: 7, inspectionLineStatus: '재입고(대기)', operatedBy: '컬리', reason: '보류해제-재입고', operatedAt: '2026-03-10T14:20:00', claimImageUrls: null },
            { historyId: 6, inspectionLineStatus: '폐기(진행중)', operatedBy: '컬리', reason: '보류해제-폐기', operatedAt: '2026-03-09T13:40:00', claimImageUrls: null },
            { historyId: 5, inspectionLineStatus: '보류(보류)', operatedBy: '컬리', reason: '클레임 승인', operatedAt: '2026-03-08T15:30:00', claimImageUrls: null },
            { historyId: 4, inspectionLineStatus: '폐기(완료)', operatedBy: '컬리', reason: '폐기 완료', operatedAt: '2026-03-07T17:00:00', claimImageUrls: null },
            { historyId: 3, inspectionLineStatus: '회송(완료)', operatedBy: '컬리', reason: '회송 완료', operatedAt: '2026-03-06T12:00:00', claimImageUrls: null },
            { historyId: 2, inspectionLineStatus: '폐기(진행중)', operatedBy: '와이즐리(woodong)', reason: '폐기 신청', operatedAt: '2026-03-05T11:10:00', claimImageUrls: null },
            { historyId: 1, inspectionLineStatus: '신청대기(정보입력대기)', operatedBy: '시스템', reason: '이슈 등록', operatedAt: '2026-03-04T10:00:00', claimImageUrls: null },
          ],
        },
        pagination: { listView: 13, listTotal: 13, cursor: 0 },
      },
    },
    {
      name: '장문 (변경주체·처리상태 긴 텍스트)',
      response: {
        data: {
          histories: [
            { historyId: 3, inspectionLineStatus: '클레임신청(클레임신청 — 거래명세서 재확인 및 수량 불일치 소명 요청)', operatedBy: '와이즐리 주식회사 김포 물류센터 입고운영팀 반품 담당 매니저(woodong.logistics.return.manager@wisely-example.co.kr)', reason: '클레임 신청 (한글 표시 누락 확인 요청 - 재검토 필요)', operatedAt: '2026-03-13T10:30:00', claimImageUrls: null },
            { historyId: 2, inspectionLineStatus: '폐기(진행중)', operatedBy: '와이즐리(woodong)', reason: '폐기 신청', operatedAt: '2026-03-05T11:10:00', claimImageUrls: null },
            { historyId: 1, inspectionLineStatus: '신청대기(정보입력대기)', operatedBy: '시스템', reason: '이슈 등록', operatedAt: '2026-03-04T10:00:00', claimImageUrls: null },
          ],
        },
        pagination: { listView: 3, listTotal: 3, cursor: 0 },
      },
    },
    {
      name: '이력 없음',
      response: {
        data: { histories: [] },
        pagination: { listView: 0, listTotal: 0, cursor: 0 },
      },
    },
    {
      name: '서버 에러 (500)',
      response: { error: { code: 500, message: '상태 변경 이력을 불러오지 못했습니다.' } },
      status: 500,
    },
  ],
});
